import React, {useEffect, useState} from 'react';
import {Badge, Button, Form, Input, Modal, Popconfirm, Select, Space, Table, Tooltip, Typography} from "antd";
import {DeleteOutlined, EditOutlined, PlusOutlined} from "@ant-design/icons";
import {useApiCreateAdminTopicContent, useApiDeleteAdminTopicContent, useApiUpdateAdminTopicContent} from "@/api/topic";
import ApiHandleSuccessMessage, {ApiHandleErrorMessage} from "@/lib/helper/apiHandleResultMessage";
import UploadCMP from "@/components/upload";
import FileViewer from "@/lib/helper/FileViewer";
import RenderIf from "@/components/render-if";
import PermList from "@/lib/helper/PermList";
import {Can} from "@/components/can/Can";

const {Text, Paragraph} = Typography;
const {Option} = Select;

function TopicContentSection({topicData, refetch}) {

    const [isModalVisible, setIsModalVisible] = useState(false);

    const [isEditMode, setIsEditMode] = useState(false);
    const [editingContentId, setEditingContentId] = useState(null);
    const [editingContentRecord, setEditingContentRecord] = useState({})
    const [contents, setContents] = useState(topicData?.data?.data?.contents || []);
    const [uploadedFileId, setUploadedFileId] = useState()

    const {mutate: createContent, isPending: isPendingCreate} = useApiCreateAdminTopicContent()
    const {mutate: updateContent, isPending: isPendingUpdate} = useApiUpdateAdminTopicContent()
    const {mutate: deleteContent, isPending: isPendingDelete} = useApiDeleteAdminTopicContent()

    const [form] = Form.useForm();

    useEffect(() => {
        setContents(topicData?.data?.data?.contents || []);
    }, [topicData]);

    function handleAddContent() {
        form.validateFields()
            .then(values => {
                createContent({
                    id: topicData?.data?.data?.id,
                    file_id: uploadedFileId,
                    ...values
                }, {
                    onSuccess: (r) => {
                        ApiHandleSuccessMessage(r)
                        setIsModalVisible(false);
                        setUploadedFileId(null)
                        form.resetFields();
                        refetch();
                    },
                    onError: (error) => {
                        ApiHandleErrorMessage(error);
                    }
                });
            })
            .catch(error => {
                console.error('Validation error:', error);
            });
    }

    function handleDeleteContent(contentId) {
        deleteContent({
            id: topicData?.data?.data?.id,
            topic_content_id: contentId
        }, {
            onSuccess: (r) => {
                ApiHandleSuccessMessage(r)
                refetch();
            },
            onError: (error) => {
                ApiHandleErrorMessage(error);
            }
        });
    }

    function handleEditContent(content) {
        setIsEditMode(true);
        setEditingContentId(content.id);
        setEditingContentRecord(content);
        form.setFieldsValue({
            title: content.title,
            content: content.content,
            audience: content.audience,
            event: content.event,
            status: content.status
        });
        setIsModalVisible(true);
    }

    function handleUpdateContent() {
        form.validateFields()
            .then(values => {
                updateContent({
                    id: topicData?.data?.data?.id,
                    topic_content_id: editingContentId,
                    file_id: uploadedFileId,
                    ...values
                }, {
                    onSuccess: (r) => {
                        ApiHandleSuccessMessage(r)
                        setIsModalVisible(false);
                        setIsEditMode(false);
                        setEditingContentRecord({})
                        setEditingContentId(null);
                        setUploadedFileId(null);
                        form.resetFields();
                        refetch();
                    },
                    onError: (error) => {
                        ApiHandleErrorMessage(error);
                    }
                });
            })
            .catch(error => {
                console.error('Validation error:', error);
            });
    }

    function handleModalOk() {
        if (isEditMode) {
            handleUpdateContent();
        } else {
            handleAddContent();
        }
    }

    function handleModalCancel() {
        setIsModalVisible(false);
        setIsEditMode(false);
        setEditingContentId(null);
        setEditingContentRecord({})
        setUploadedFileId(null);
        form.resetFields();
    }

    function handleUploadFinish(file) {
        if (file?.file?.response?.data?.id) {
            setUploadedFileId(file?.file?.response?.data?.id)
        }

    }


    const contentColumns = [
        {
            title: 'عنوان',
            dataIndex: 'title',
            key: 'title',
            render: (text) => <Text strong>{text}</Text>,
        },
        {
            title: 'محتوا',
            dataIndex: 'content',
            key: 'content',
            render: (text, record) => (
                <>
                    <Paragraph ellipsis={{rows: 2, expandable: true}}>
                        {text}
                    </Paragraph>
                    <RenderIf condition={!!record?.file?.id}>
                        <FileViewer fileUrl={record?.file?.url}/>
                    </RenderIf>
                </>

            ),
        },
        // {
        //     title: 'مخاطب',
        //     dataIndex: 'audience',
        //     key: 'audience',
        //     render: (audience) => (
        //         <Tag color={audience === 'student' ? 'blue' : 'green'}>
        //             {audience === 'student' ? 'دانش‌آموز' : 'معلم'}
        //         </Tag>
        //     ),
        // },
        // {
        //     title: 'زمان نمایش',
        //     dataIndex: 'event',
        //     key: 'event',
        //     render: (event) => (
        //         <Tag color={event === 'after_register' ? 'purple' : 'orange'}>
        //             {event === 'after_register' ? 'بعد از ثبت‌نام' : 'قبل از ثبت‌نام'}
        //         </Tag>
        //     ),
        // },
        {
            title: 'وضعیت',
            dataIndex: 'status',
            key: 'status',
            render: (status) => (
                <Badge status={status === 'published' ? 'success' : 'warning'}
                       text={status === 'published' ? 'منتشر شده' : 'پیش‌نویس'}/>
            ),
        },
        {
            title: 'عملیات',
            key: 'action',
            render: (_, record) => (
                <Space size="middle">
                    <Can permission={PermList.TOPIC_CONTENT_EDIT}>
                        <Tooltip title="ویرایش">
                            <Button
                                type="primary"
                                icon={<EditOutlined/>}
                                size="small"
                                onClick={() => handleEditContent(record)}
                            />
                        </Tooltip>
                    </Can>
                    <Can permission={PermList.TOPIC_CONTENT_DELETE}>
                        <Tooltip title="حذف">
                            <Popconfirm
                                onConfirm={() => handleDeleteContent(record.id)}
                                title={'آیا از حذف این محتوا اطمینان دارید؟'}
                                okText="بله"
                                cancelText="خیر"
                            >
                                <Button danger icon={<DeleteOutlined/>} size="small"/>
                            </Popconfirm>
                        </Tooltip>
                    </Can>
                </Space>
            ),
        },
    ];


    return (
        <div>
            <div className="mb-4">
                <Can permission={PermList.TOPIC_CONTENT_CREATE}>
                    <Button
                        type="primary"
                        icon={<PlusOutlined/>}
                        onClick={() => {
                            setIsEditMode(false);
                            setEditingContentId(null);
                            form.resetFields();
                            setIsModalVisible(true);
                        }}
                        className="mb-4"
                    >
                        افزودن محتوای جدید
                    </Button>
                </Can>
                <Table
                    columns={contentColumns}
                    dataSource={contents}
                    rowKey={(record) => record.id || Math.random().toString()}
                    pagination={{pageSize: 5}}
                    className="mt-4"
                    loading={isPendingCreate || isPendingUpdate || isPendingDelete}
                />
            </div>

            <Modal
                title={isEditMode ? "ویرایش محتوا" : "افزودن محتوای جدید"}
                open={isModalVisible}
                onOk={handleModalOk}
                onCancel={handleModalCancel}
                width={600}
                okText={isEditMode ? "به‌روزرسانی" : "ذخیره"}
                cancelText="انصراف"
                confirmLoading={isPendingCreate || isPendingUpdate}
            >
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={{
                        audience: 'student',
                        event: 'after_register',
                        status: 'published'
                    }}
                >
                    <Form.Item
                        name="title"
                        label="عنوان"
                        rules={[{required: true, message: 'لطفاً عنوان را وارد کنید'}]}
                    >
                        <Input placeholder="عنوان محتوا را وارد کنید"/>
                    </Form.Item>

                    <Form.Item
                        name="content"
                        label="محتوا"
                        rules={[{required: false, message: 'لطفاً محتوا را وارد کنید'}]}
                    >
                        <Input.TextArea rows={4} placeholder="متن محتوا را وارد کنید"/>
                    </Form.Item>

                    <Form.Item
                        name="file"
                        label="فایل"

                    >
                        <RenderIf condition={!!editingContentRecord?.file?.id}>
                            <div className={'my-2'}>

                                <FileViewer fileUrl={editingContentRecord?.file?.url}/>
                            </div>
                        </RenderIf>
                        <UploadCMP dragMode={true} afterUploadFn={handleUploadFinish}/>
                    </Form.Item>

                    {/*<Form.Item*/}
                    {/*    name="audience"*/}
                    {/*    label="مخاطب"*/}
                    {/*    rules={[{required: true, message: 'لطفاً مخاطب را انتخاب کنید'}]}*/}
                    {/*>*/}
                    {/*    <Select>*/}
                    {/*        <Option value="student">دانش‌آموز</Option>*/}
                    {/*        <Option value="teacher">معلم</Option>*/}
                    {/*    </Select>*/}
                    {/*</Form.Item>*/}

                    {/*<Form.Item*/}
                    {/*    name="event"*/}
                    {/*    label="زمان نمایش"*/}
                    {/*    rules={[{required: true, message: 'لطفاً زمان نمایش را انتخاب کنید'}]}*/}
                    {/*>*/}
                    {/*    <Select>*/}
                    {/*        <Option value="after_register">بعد از ثبت‌نام</Option>*/}
                    {/*        <Option value="before_register">قبل از ثبت‌نام</Option>*/}
                    {/*    </Select>*/}
                    {/*</Form.Item>*/}

                    <Form.Item
                        name="sort"
                        label="ترتیب"
                    >
                        <Input type={'number'} placeholder="ترتیب"/>
                    </Form.Item>

                    <Form.Item
                        name="status"
                        label="وضعیت"
                        rules={[{required: true, message: 'لطفاً وضعیت را انتخاب کنید'}]}
                    >
                        <Select>
                            <Option value="published">منتشر شده</Option>
                            <Option value="draft">پیش‌نویس</Option>
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
}

export default TopicContentSection;